import axios from 'axios';

export interface ApiException {
  domain: string;
  type: string;
  devMsg?: string;
  data: { [key: string]: string };
  /** The HTTP status it came with. Not part of the body: `request()` attaches it. */
  status?: number;
}

// Backend permission denial (403). Expected when a user lacks access — not a
// fetch failure, so callers should treat it as "no access", not an error toast.
export const isPermissionDenied = (e: unknown): boolean =>
  !!e &&
  typeof e === 'object' &&
  (e as ApiException).domain === 'MIDDLEWARES' &&
  (e as ApiException).type === 'ROLE';

// The record is gone (404). Every service reports it with the shared NOT_FOUND type, and
// api.ts throws the response body rather than the axios error, so the status is not there.
export const isNotFound = (e: unknown): boolean =>
  !!e && typeof e === 'object' && (e as ApiException).type === 'NOT_FOUND';

// The attempt budget is spent (429), so the server has burned whatever secret the attempts were against.
export const isTooManyAttempts = (e: unknown): boolean =>
  !!e && typeof e === 'object' && (e as ApiException).type === 'TOO_MANY_ATTEMPTS';

// The invitation secret is gone: it expired or the invitation was already accepted (410).
export const isInviteExpired = (e: unknown): boolean =>
  !!e &&
  typeof e === 'object' &&
  (e as ApiException).domain === 'BASIC_AUTH' &&
  (e as ApiException).type === 'INVITE_EXPIRED';

// Gated access is on and the contact isn't on the access list (403). Sign-in, sign-up, magic
// links, OAuth, invitations and token refresh all answer with it.
export const isAccessLimited = (e: unknown): boolean =>
  !!e &&
  typeof e === 'object' &&
  (e as ApiException).domain === 'WHITELIST' &&
  (e as ApiException).type === 'ACCESS_LIMITED';

export const ACCESS_LIMITED_MESSAGE =
  'Access to VesselHaven is limited right now. Please contact our team to request access.';

// A plan change would leave the owner or a co-owner without a management seat (409).
export const isSeatsBelowOwnerGroup = (e: unknown): boolean =>
  !!e && typeof e === 'object' && (e as ApiException).type === 'SEATS_BELOW_OWNER_GROUP';

export const getErrorStatus = (error: unknown): number | undefined => {
  if (axios.isAxiosError(error)) return error.response?.status;
  if (!!error && typeof error === 'object') return (error as ApiException).status;
  return undefined;
};

/**
 * A video or audio file the media encoder has not finished with yet (409): every
 * service refuses its bytes until the web-playable copy replaces them. Not a
 * failure — the same request succeeds once it is ready.
 */
export const isMediaNotReady = (error: unknown): boolean =>
  getErrorStatus(error) === 409 && (error as ApiException).type === 'INVALID_STATE';

/**
 * Why an uploaded image was refused (413), or null when it was not. A decompression bomb — a
 * huge canvas in a tiny file — carries its pixel count; an image over the resolution limit does not.
 */
export const imageRefusalMessage = (error: unknown): null | string => {
  if (getErrorStatus(error) !== 413 || (error as ApiException).domain !== 'IMAGE') return null;

  return (error as ApiException).data?.pixels !== undefined
    ? 'Please choose a different image.'
    : 'Please choose a smaller image.';
};

const UNREADABLE_PDF_MESSAGE =
  'Couldn’t read this PDF. If it’s a scanned image or password-protected, export the statement as PDF text, CSV or Excel from your bank.';

/**
 * Why the statement import refused a file (400 CARD_IMPORT), or null to fall back to the generic
 * message. The backend's own wording is in `dev_message` (never `error_message`, which is
 * technical), but the backend blanks `dev_message` in prod — so a refused PDF without it still
 * gets an actionable message.
 */
export const cardImportRefusalMessage = (error: unknown, isPdf: boolean): null | string => {
  if (getErrorStatus(error) !== 400 || (error as ApiException).domain !== 'CARD_IMPORT')
    return null;

  const devMessage = (error as { dev_message?: unknown }).dev_message;
  if (typeof devMessage === 'string' && devMessage.trim()) return devMessage.trim();

  return isPdf ? UNREADABLE_PDF_MESSAGE : null;
};

/**
 * The body of an error response as the exception `request()` throws, with its
 * status attached. A blob request gets its error body as a Blob too, so that one
 * is read as the JSON it is — otherwise neither its domain (a token refresh
 * hinges on it) nor its type would be visible to anyone.
 */
export const toApiException = async (response: {
  data: unknown;
  status: number;
}): Promise<ApiException> => {
  let body = response.data;
  if (body instanceof Blob) {
    try {
      body = JSON.parse(await body.text());
    } catch {
      body = {};
    }
  }
  if (!body || typeof body !== 'object') return body as ApiException;

  Object.defineProperty(body, 'status', { value: response.status, enumerable: false });
  return body as ApiException;
};
